import type {
  LockerverseAuction,
  LockerverseAuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import {
  ChevronDown,
  Clock3,
  Gavel,
  ShieldCheck,
  TimerReset,
} from "lucide-react";
import { useId, useState } from "react";
import { AuctionDescription } from "./auction-description.tsx";
import {
  formatAuctionBoundary,
  formatAuctionMoney as formatMoney,
  getAuctionDisplayStatus,
} from "./auction-display.ts";
import { AuctionGallery } from "./auction-gallery.tsx";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

function metricIcon(label: string | undefined) {
  if (label === "Buy Now") {
    return <ShieldCheck aria-hidden="true" size={16} />;
  }
  if (label === "Available" || label === "Increment") {
    return <TimerReset aria-hidden="true" size={16} />;
  }
  return <Gavel aria-hidden="true" size={16} />;
}
function itemMetrics(
  item: LockerverseAuctionItem,
  status: LockerverseAuctionStatus
) {
  if (item.saleMode === "buy_now") {
    return [
      ["Buy Now", formatMoney(item.buyNowPriceAmount ?? 0)],
      [
        "Available",
        item.availableQuantity === null
          ? "Available"
          : String(item.availableQuantity),
      ],
    ];
  }
  if (status === "finished") {
    return [
      [
        "Final bid",
        item.maxBidAmount ? formatMoney(item.maxBidAmount) : "No bids",
      ],
    ];
  }
  return [
    ["Current bid", formatMoney(item.maxBidAmount ?? 0)],
    ["Floor", formatMoney(item.minBidAmount ?? 0)],
    ["Increment", formatMoney(item.bidIncrementAmount ?? 100)],
  ];
}
export function AuctionItemDetails({
  auction,
  item,
}: {
  auction: LockerverseAuction;
  item: LockerverseAuctionItem;
}) {
  useAuctionBoundary(
    item.startAt ?? auction.startAt,
    item.endAt ?? auction.endAt
  );
  const [expanded, setExpanded] = useState(false);
  const descriptionId = useId();
  const status = getAuctionDisplayStatus(auction, item);
  const boundaryLabel = {
    active: "Closes",
    draft: "Opens",
    finished: "Closed",
  }[status];
  const boundary = formatAuctionBoundary(auction, status, item);
  const buyNow = item.saleMode === "buy_now";
  const longDescription = (item.description ?? "").trim().length > 240;
  const metrics = itemMetrics(item, status);
  return (
    <section className="lockerverse-auctions__item-summary">
      <AuctionGallery
        fallbackImage={
          item.image ??
          auction.configuration.image ??
          auction.community?.banner ??
          auction.community?.thumbnail ??
          null
        }
        images={item.images}
        name={item.name}
      />
      <div className="lockerverse-auctions__item-copy">
        <h2>{item.name}</h2>
        {item.description ? (
          <div className="lockerverse-auctions__item-description">
            <AuctionDescription
              className={
                !expanded && longDescription
                  ? "lockerverse-auctions__description-clamped"
                  : undefined
              }
              description={item.description}
              id={descriptionId}
            />
            {longDescription ? (
              <button
                aria-controls={descriptionId}
                aria-expanded={expanded}
                className="lockerverse-auctions__description-toggle"
                onClick={() => setExpanded(!expanded)}
                type="button"
              >
                {expanded ? "Hide description" : "Read full description"}{" "}
                <ChevronDown
                  aria-hidden="true"
                  size={16}
                  style={{ transform: expanded ? "rotate(180deg)" : undefined }}
                />
              </button>
            ) : null}
          </div>
        ) : null}
        <dl className="lockerverse-auctions__stat-panel">
          {metrics.map(([label, value]) => (
            <div className="lockerverse-auctions__stat-row" key={label}>
              <dt>
                {metricIcon(label)}
                {label}
              </dt>
              <dd>{value}</dd>
            </div>
          ))}
          <div className="lockerverse-auctions__stat-row">
            <dt>
              <Clock3 aria-hidden="true" size={16} />
              {boundaryLabel}
            </dt>
            <dd className="lockerverse-auctions__stat-date">{boundary}</dd>
          </div>
          {!buyNow && status === "finished" ? (
            <div className="lockerverse-auctions__stat-footer">
              <div>
                <dt>Floor</dt>
                <dd>{formatMoney(item.minBidAmount ?? 0)}</dd>
              </div>
              <div>
                <dt>Bid increment</dt>
                <dd>{formatMoney(item.bidIncrementAmount ?? 100)}</dd>
              </div>
            </div>
          ) : null}
        </dl>
      </div>
    </section>
  );
}
